// @tennis-matches/services/winterdoppel/pairing

import {
  sortReservesByFairness,
  suggestWinterDoppelCourtMix,
} from "./planning.js";
import {
  normalizeWinterDoppelPlayLevel,
  WINTERDOPPEL_DEFAULT_PLAY_LEVEL,
} from "./players.js";

export { WINTERDOPPEL_DEFAULT_PLAY_LEVEL };

/**
 * @param {Array<{ id?: string, play_level?: number }>|null|undefined} players
 * @returns {Map<string, number>}
 */
export function buildPlayerLevelMap(players = []) {
  const map = new Map();
  for (const p of players) {
    const id = String(p?.id || "");
    if (!id) continue;
    map.set(id, normalizeWinterDoppelPlayLevel(p.play_level));
  }
  return map;
}

function __levelFor(id, playerLevels) {
  if (playerLevels instanceof Map) {
    return playerLevels.get(String(id)) ?? WINTERDOPPEL_DEFAULT_PLAY_LEVEL;
  }
  return WINTERDOPPEL_DEFAULT_PLAY_LEVEL;
}

function __avgLevel(ids, playerLevels) {
  const list = (ids || []).map((id) => __levelFor(id, playerLevels));
  if (!list.length) return WINTERDOPPEL_DEFAULT_PLAY_LEVEL;
  return list.reduce((sum, n) => sum + n, 0) / list.length;
}

function __sortCourtUnits(units, playerLevels) {
  if (!playerLevels?.size) return units;
  return [...units].sort((a, b) => {
    const idsA = a.pairs.flat();
    const idsB = b.pairs.flat();
    const diff =
      __avgLevel(idsB, playerLevels) - __avgLevel(idsA, playerLevels);
    if (diff !== 0) return diff;
    return idsA.join("|").localeCompare(idsB.join("|"));
  });
}

/**
 * Wählt bis zu `size` Spieler mit möglichst ähnlicher Spielstärke.
 * Seed = stärkster verbleibender Spieler, dann geringste Abstandssumme.
 */
function __pickLevelCluster(pool, size, playerLevels) {
  const arr = [...pool].map(String).filter(Boolean);
  if (arr.length <= size) return arr;
  arr.sort((a, b) => {
    const d = __levelFor(b, playerLevels) - __levelFor(a, playerLevels);
    if (d !== 0) return d;
    return a.localeCompare(b);
  });
  const seed = arr[0];
  const seedLevel = __levelFor(seed, playerLevels);
  const rest = arr.slice(1).sort((a, b) => {
    const da = Math.abs(__levelFor(a, playerLevels) - seedLevel);
    const db = Math.abs(__levelFor(b, playerLevels) - seedLevel);
    if (da !== db) return da - db;
    const dAvg = __levelFor(b, playerLevels) - __levelFor(a, playerLevels);
    if (dAvg !== 0) return dAvg;
    return a.localeCompare(b);
  });
  return [seed, ...rest.slice(0, size - 1)];
}

function __preferredPairKeys(preferredPairs = []) {
  const keys = new Set();
  for (const raw of preferredPairs || []) {
    const a = String(raw?.[0] || "");
    const b = String(raw?.[1] || "");
    if (!a || !b || a === b) continue;
    keys.add(partnerHistoryKey(a, b));
  }
  return keys;
}

function __pairScore(a, b, partnerHistory, preferredKeys) {
  const key = partnerHistoryKey(a, b);
  const history = partnerHistory.get(key) || 0;
  const preferred = preferredKeys?.has(key) ? -1_000_000 : 0;
  return history + preferred;
}

function __orderIdsForPreferredPairs(ids, preferredPairs = []) {
  const list = [...new Set((ids || []).map(String).filter(Boolean))];
  const ordered = [];
  const used = new Set();
  for (const raw of preferredPairs || []) {
    const a = String(raw?.[0] || "");
    const b = String(raw?.[1] || "");
    if (!a || !b || a === b) continue;
    if (!list.includes(a) || !list.includes(b)) continue;
    if (used.has(a) || used.has(b)) continue;
    ordered.push(a, b);
    used.add(a);
    used.add(b);
  }
  for (const id of list) {
    if (!used.has(id)) ordered.push(id);
  }
  return ordered;
}

/**
 * Stable unordered partner key.
 * @param {string} a
 * @param {string} b
 */
export function partnerHistoryKey(a, b) {
  const x = String(a ?? "");
  const y = String(b ?? "");
  return x < y ? `${x}|${y}` : `${y}|${x}`;
}

/**
 * Count how often two starters shared the same court + pair_slot.
 * @param {Array<{ player_id: string, role?: string, court_no?: number|null, pair_slot?: number|null }>} sessionPlayers
 * @returns {Map<string, number>}
 */
export function buildPartnerHistoryCounts(sessionPlayers = []) {
  /** @type {Map<string, number>} */
  const counts = new Map();
  /** @type {Map<string, string[]>} session|court|slot -> playerIds */
  const buckets = new Map();

  for (const sp of sessionPlayers) {
    if (!sp || sp.role === "reserve") continue;
    const court = Number(sp.court_no);
    const slot = Number(sp.pair_slot);
    if (!Number.isFinite(court) || court < 1) continue;
    if (!Number.isFinite(slot) || (slot !== 0 && slot !== 1)) continue;
    const pid = String(sp.player_id ?? "");
    if (!pid) continue;
    const key = `${sp.session_id || ""}|${court}|${slot}`;
    const list = buckets.get(key) || [];
    list.push(pid);
    buckets.set(key, list);
  }

  for (const list of buckets.values()) {
    const uniq = [...new Set(list)];
    for (let i = 0; i < uniq.length; i += 1) {
      for (let j = i + 1; j < uniq.length; j += 1) {
        const k = partnerHistoryKey(uniq[i], uniq[j]);
        counts.set(k, (counts.get(k) || 0) + 1);
      }
    }
  }
  return counts;
}

/**
 * Record newly assigned pairs into an existing history map (mutates).
 * @param {Map<string, number>} history
 * @param {Map<string, { court_no: number, pair_slot: number }>} assignments
 */
export function recordPartnerAssignments(history, assignments) {
  /** @type {Map<string, string[]>} */
  const buckets = new Map();
  for (const [playerId, asg] of assignments.entries()) {
    if (!asg) continue;
    const key = `${asg.court_no}|${asg.pair_slot}`;
    const list = buckets.get(key) || [];
    list.push(String(playerId));
    buckets.set(key, list);
  }
  for (const list of buckets.values()) {
    const uniq = [...new Set(list)];
    for (let i = 0; i < uniq.length; i += 1) {
      for (let j = i + 1; j < uniq.length; j += 1) {
        const k = partnerHistoryKey(uniq[i], uniq[j]);
        history.set(k, (history.get(k) || 0) + 1);
      }
    }
  }
}

/**
 * Greedy doubles pairing + court assignment with partner-history fairness.
 * Two players with same court_no + pair_slot are partners; two pair_slots per court.
 *
 * @param {{
 *   playerIds: string[],
 *   courtCount: number,
 *   partnerHistory?: Map<string, number>,
 *   preferredPairs?: Array<[string, string]>,
 *   forcedPairs?: Array<[string, string]>,
 *   forcedGroups?: string[][],
 *   playerLevels?: Map<string, number>,
 * }} opts
 * @returns {Map<string, { court_no: number, pair_slot: number }>}
 */
export function assignCourtsAndPairs({
  playerIds = [],
  courtCount = 1,
  partnerHistory = new Map(),
  preferredPairs = [],
  forcedPairs = [],
  forcedGroups = [],
  playerLevels = new Map(),
} = {}) {
  const courts = Math.max(1, Number(courtCount) || 1);
  const ids = [...new Set((playerIds || []).map(String).filter(Boolean))];
  /** @type {Map<string, { court_no: number, pair_slot: number }>} */
  const assignments = new Map();
  if (ids.length < 2) return assignments;

  const bondPreferences =
    (preferredPairs || []).length > 0 ? preferredPairs : forcedPairs;
  const preferredKeys = __preferredPairKeys(bondPreferences);

  const remaining = new Set(ids);
  /** @type {Array<[string, string]>} */
  const pairs = [];
  /** @type {Array<[[string, string], [string, string]]>} */
  const courtBlocks = [];

  const pickBestPair = (pool) => {
    const arr = [...pool].sort((a, b) => a.localeCompare(b));
    if (arr.length < 2) return null;
    let bestA = arr[0];
    let bestB = arr[1];
    let bestScore = __pairScore(bestA, bestB, partnerHistory, preferredKeys);
    for (let i = 0; i < arr.length; i += 1) {
      for (let j = i + 1; j < arr.length; j += 1) {
        const a = arr[i];
        const b = arr[j];
        const score = __pairScore(a, b, partnerHistory, preferredKeys);
        if (
          score < bestScore ||
          (score === bestScore && `${a}|${b}` < `${bestA}|${bestB}`)
        ) {
          bestA = a;
          bestB = b;
          bestScore = score;
        }
      }
    }
    return bestA < bestB ? [bestA, bestB] : [bestB, bestA];
  };

  const groups = (forcedGroups || [])
    .map((g) => [
      ...new Set((Array.isArray(g) ? g : []).map(String).filter(Boolean)),
    ])
    .filter((g) => g.length >= 2)
    .sort((a, b) => b.length - a.length);

  for (const group of groups) {
    const pool = new Set(group.filter((id) => remaining.has(id)));
    while (pool.size >= 4) {
      const clusterIds =
        playerLevels?.size > 0
          ? __pickLevelCluster(pool, 4, playerLevels)
          : [...pool].sort((a, b) => a.localeCompare(b)).slice(0, 4);
      const cluster = new Set(clusterIds);
      const p1 = pickBestPair(cluster);
      if (!p1) break;
      cluster.delete(p1[0]);
      cluster.delete(p1[1]);
      const p2 = pickBestPair(cluster);
      if (!p2) {
        pairs.push(p1);
        remaining.delete(p1[0]);
        remaining.delete(p1[1]);
        pool.delete(p1[0]);
        pool.delete(p1[1]);
        break;
      }
      courtBlocks.push([p1, p2]);
      for (const id of [p1[0], p1[1], p2[0], p2[1]]) {
        remaining.delete(id);
        pool.delete(id);
      }
    }
    while (pool.size >= 2) {
      const p = pickBestPair(pool);
      if (!p) break;
      pool.delete(p[0]);
      pool.delete(p[1]);
      pairs.push(p);
      remaining.delete(p[0]);
      remaining.delete(p[1]);
    }
  }

  // Rest: zuerst stärkenähnliche Vierer-Doppel, dann Restpaare.
  while (remaining.size >= 4 && playerLevels?.size > 0) {
    const clusterIds = __pickLevelCluster(remaining, 4, playerLevels);
    const cluster = new Set(clusterIds);
    const p1 = pickBestPair(cluster);
    if (!p1) break;
    cluster.delete(p1[0]);
    cluster.delete(p1[1]);
    const p2 = pickBestPair(cluster);
    if (!p2) {
      pairs.push(p1);
      remaining.delete(p1[0]);
      remaining.delete(p1[1]);
      break;
    }
    courtBlocks.push([p1, p2]);
    for (const id of [p1[0], p1[1], p2[0], p2[1]]) {
      remaining.delete(id);
    }
  }

  while (remaining.size >= 2) {
    const p = pickBestPair(remaining);
    if (!p) break;
    pairs.push(p);
    remaining.delete(p[0]);
    remaining.delete(p[1]);
  }

  const mix = suggestWinterDoppelCourtMix({
    pool: ids.length,
    courtCount: courts,
  });

  /** @type {Array<[[string, string], [string, string]]>} */
  const doubles = courtBlocks.slice(0, mix.doubles);
  /** @type {Array<[string, string]>} */
  const leftoverPairs = [
    ...courtBlocks.slice(mix.doubles).flatMap((block) => block),
    ...pairs,
  ];
  while (doubles.length < mix.doubles && leftoverPairs.length >= 2) {
    doubles.push([leftoverPairs.shift(), leftoverPairs.shift()]);
  }

  /** @type {Array<{ pairs: Array<[string, string]> }>} */
  const courtUnits = [
    ...doubles.map((block) => ({ pairs: block })),
    ...leftoverPairs.slice(0, mix.singles).map((pair) => ({ pairs: [pair] })),
  ];

  const sortedUnits = __sortCourtUnits(courtUnits, playerLevels);

  let courtNo = 1;
  for (const unit of sortedUnits) {
    if (courtNo > courts) break;
    let pairSlot = 0;
    for (const [a, b] of unit.pairs) {
      if (courtNo > courts) break;
      assignments.set(a, { court_no: courtNo, pair_slot: pairSlot });
      assignments.set(b, { court_no: courtNo, pair_slot: pairSlot });
      pairSlot += 1;
      if (pairSlot >= 2) {
        pairSlot = 0;
        courtNo += 1;
      }
    }
    if (pairSlot !== 0) courtNo += 1;
  }

  return assignments;
}

/**
 * Training: jede gewählte Gruppe bekommt einen Platz; alle Mitglieder
 * (auch ungerade Anzahl) erhalten court_no – keine „Warteliste“-Unpaired.
 * @returns {Map<string, { court_no: number, pair_slot: number }>}
 */
export function assignTrainingGroupCourts({
  groupSchedules = [],
  starterIds = [],
  courtCount = 1,
  playerLevels = new Map(),
  preferredPairs = [],
  forcedPairs = [],
} = {}) {
  const starterSet = new Set((starterIds || []).map(String).filter(Boolean));
  const courts = Math.max(1, Number(courtCount) || 1);
  const bondPreferences =
    (preferredPairs || []).length > 0 ? preferredPairs : forcedPairs;
  /** @type {Map<string, { court_no: number, pair_slot: number }>} */
  const assignments = new Map();
  if (!starterSet.size) return assignments;

  const groups = (groupSchedules || [])
    .map((gs) => ({
      id: String(gs.group?.id || gs.id || ""),
      members: __orderIdsForPreferredPairs(
        [...new Set((gs.playerIds || []).map(String).filter(Boolean))].filter(
          (id) => starterSet.has(id),
        ),
        bondPreferences,
      ),
    }))
    .filter((g) => g.members.length > 0)
    .sort((a, b) => {
      if (playerLevels?.size) {
        const diff =
          __avgLevel(b.members, playerLevels) -
          __avgLevel(a.members, playerLevels);
        if (diff !== 0) return diff;
      }
      return b.members.length - a.members.length || a.id.localeCompare(b.id);
    });

  let courtNo = 1;
  for (const g of groups) {
    if (courtNo > courts) break;
    g.members.forEach((id, idx) => {
      // DB erlaubt nur pair_slot 0|1 (max. 2 Doppel/Platz); 5. Spieler → Slot 1.
      assignments.set(id, {
        court_no: courtNo,
        pair_slot: Math.min(1, Math.floor(idx / 2)),
      });
    });
    courtNo += 1;
  }

  const fallbackCourt = Math.min(courts, Math.max(1, courtNo - 1));
  for (const id of starterSet) {
    if (!assignments.has(id)) {
      assignments.set(id, { court_no: fallbackCourt, pair_slot: 0 });
    }
  }
  return assignments;
}

/**
 * Group starter session-players into courts → pairs for UI / WhatsApp.
 * @param {Array<object>} sessionPlayers
 * @param {Map<string, { display_name?: string }>|Record<string, { display_name?: string }>} playerById
 */
export function buildWinterDoppelCourtGroups(sessionPlayers = [], playerById) {
  const lookup = (id) => {
    if (playerById instanceof Map) return playerById.get(id);
    return playerById?.[id];
  };

  const starters = (sessionPlayers || []).filter(
    (sp) => sp.role === "starter" && sp.rsvp !== "no",
  );
  /** @type {Map<number, Map<number, object[]>>} */
  const byCourt = new Map();
  /** @type {object[]} */
  const unpaired = [];

  for (const sp of starters) {
    const court = Number(sp.court_no);
    const slot = Number(sp.pair_slot);
    if (!Number.isFinite(court) || court < 1 || (slot !== 0 && slot !== 1)) {
      unpaired.push(sp);
      continue;
    }
    if (!byCourt.has(court)) byCourt.set(court, new Map());
    const slots = byCourt.get(court);
    if (!slots.has(slot)) slots.set(slot, []);
    slots.get(slot).push(sp);
  }

  const courts = [...byCourt.keys()]
    .sort((a, b) => a - b)
    .map((courtNo) => {
      const slots = byCourt.get(courtNo);
      const pairs = [...slots.keys()]
        .sort((a, b) => a - b)
        .map((pairSlot) => {
          const sps = slots.get(pairSlot) || [];
          const names = sps.map(
            (sp) => lookup(sp.player_id)?.display_name || sp.player_id,
          );
          return { pairSlot, sessionPlayers: sps, names };
        });
      return { courtNo, pairs };
    });

  return { courts, unpaired };
}

/**
 * Mindestens ein Starter mit Platz + Paar-Slot (Detailplanung aktiv).
 * @param {Array<object>} sessionPlayers
 */
export function hasWinterDoppelSessionPairings(sessionPlayers = []) {
  return (sessionPlayers || []).some((sp) => {
    if (!sp || sp.role !== "starter" || sp.rsvp === "no") return false;
    const court = Number(sp.court_no);
    const slot = Number(sp.pair_slot);
    return Number.isFinite(court) && court >= 1 && (slot === 0 || slot === 1);
  });
}

/**
 * Stabile Anzeige-Reihenfolge für Warteliste (Fairness-Quote, falls Kontext übergeben).
 * @param {Array<object>} sessionPlayers
 * @param {{ starterCounts?: Map, availabilityCounts?: Map, participationCaps?: Map }|null} fairnessOpts
 */
export function sortForWaitlistDisplay(
  sessionPlayers = [],
  fairnessOpts = null,
) {
  const list = (sessionPlayers || []).slice();
  if (
    fairnessOpts &&
    (fairnessOpts.starterCounts || fairnessOpts.availabilityCounts)
  ) {
    return sortReservesByFairness(list, fairnessOpts);
  }
  return list.sort((a, b) => {
    const ra = Number(a.waitlist_rank);
    const rb = Number(b.waitlist_rank);
    if (Number.isFinite(ra) && Number.isFinite(rb) && ra !== rb) {
      return ra - rb;
    }
    if (Number.isFinite(ra) && !Number.isFinite(rb)) return -1;
    if (!Number.isFinite(ra) && Number.isFinite(rb)) return 1;
    const ca = String(a.created_at || "");
    const cb = String(b.created_at || "");
    if (ca !== cb) return ca.localeCompare(cb);
    return String(a.player_id || "").localeCompare(String(b.player_id || ""));
  });
}
